import { useCallback, useEffect, useMemo, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Inline } from '@spark-web/inline';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import {
  OPEN_BANKING_INCOME_DETAILS,
  OPEN_BANKING_POLLING_INTERVAL,
  OPEN_BANKING_TIMEOUT_DURATION,
  THREE_MINUTES_MS,
  TWO_MINUTES_MS,
} from '../../utils/constants';

type UserChoice = 'waiting' | 'continue' | 'cancel' | null;

interface OpenBankingIncomeLoadingDetailsProps {
  startTime: number | null;
  onCancel: () => void;
  onError: () => void;
  onUserChoice: (choice: UserChoice) => void;
}

enum LoadingState {
  initial = 'initial',
  longer_initial_wait = 'longer_initial_wait',
  user_choice = 'user_choice',
  patience = 'patience',
  cancel = 'cancel',
}

export default function OpenBankingIncomeLoadingDetails({
  startTime,
  onCancel,
  onError,
  onUserChoice,
}: OpenBankingIncomeLoadingDetailsProps) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [userChoice, setUserChoice] = useState<UserChoice>(null);

  const timeConditions = useMemo(
    () => ({
      isOverTwoMinutes: elapsedTime >= TWO_MINUTES_MS,
      isOverThreeMinutes: elapsedTime >= THREE_MINUTES_MS,
      isOverTenMinutes: elapsedTime >= OPEN_BANKING_TIMEOUT_DURATION,
    }),
    [elapsedTime],
  );

  // Determine current loading state based on time elapsed and user choices
  const loadingState = useMemo((): LoadingState => {
    // Note: 10+ min timeout is handled by useEffect calling onError, not by showing timeout UI
    if (timeConditions.isOverThreeMinutes && userChoice === null) return LoadingState.user_choice; // 3+ min + no user choice yet
    if (userChoice === 'continue') return LoadingState.patience; // User chose "Keep waiting"
    if (userChoice === 'cancel') return LoadingState.cancel; // User chose "Try another option"
    if (timeConditions.isOverTwoMinutes) return LoadingState.longer_initial_wait; // 2+ min + no user interaction
    return LoadingState.initial; // 0-2 min
  }, [timeConditions, userChoice]);

  useEffect(() => {
    if (!startTime) return;

    const updateElapsedTime = () => {
      setElapsedTime(Date.now() - startTime);
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, OPEN_BANKING_POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [startTime]);

  // Handle 10-minute timeout - call onError to trigger default error handling
  useEffect(() => {
    if (timeConditions.isOverTenMinutes) {
      // Set user choice to cancel to stop polling
      onUserChoice('cancel');
      // Call error handler to update parent state
      onError();
    }
  }, [timeConditions.isOverTenMinutes, onError, onUserChoice]);

  // Log datadog action when income retrieval is taking longer than expected (2-3 minutes)
  useEffect(() => {
    if (loadingState === LoadingState.longer_initial_wait && startTime) {
      datadogRum.addAction('Income retrieval taking longer than expected', {
        name: 'Retrieving income is taking more than a minutes',
        values: {
          start_time: startTime,
          time_diff_minutes: elapsedTime / (60 * 1000),
        },
      });
    }
  }, [loadingState, startTime, elapsedTime]);

  const handleUserChoice = useCallback(
    (choice: 'continue' | 'cancel') => {
      setUserChoice(choice);
      onUserChoice(choice);

      if (choice === 'cancel') {
        onCancel();
      }
    },
    [onUserChoice, onCancel],
  );

  const handleKeepWaiting = useCallback(() => {
    setUserChoice('continue');
    onUserChoice('continue');
  }, [onUserChoice]);

  const LoadingContainer = ({ children }: { children: React.ReactNode }) => (
    <Box background="primaryLow" borderRadius="medium" paddingX="xlarge" paddingY="xxxlarge">
      <Stack gap="large" align="center">
        <Box>
          <Spinner size="xsmall" tone="positive" />
        </Box>
        {children}
      </Stack>
    </Box>
  );

  const TextContent = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <>
      <Box paddingX="xxlarge">
        <Text weight="semibold" size="large" align="center">
          {title}
        </Text>
      </Box>
      <Box paddingY="xsmall">
        <Text align="center" tone="muted">
          {subtitle}
        </Text>
      </Box>
    </>
  );

  const renderContent = () => {
    switch (loadingState) {
      case LoadingState.user_choice: // After 3+ minutes with no user response - asks user to choose
        return (
          <LoadingContainer>
            <TextContent
              title={OPEN_BANKING_INCOME_DETAILS.LOADING_TITLE_3MIN_QUESTION}
              subtitle={OPEN_BANKING_INCOME_DETAILS.LOADING_SUBTITLE_3MIN}
            />
            <Inline gap="medium">
              <Button prominence="high" onClick={() => handleUserChoice('continue')}>
                Keep waiting
              </Button>
              <Button prominence="low" onClick={() => handleUserChoice('cancel')}>
                Try another option
              </Button>
            </Inline>
          </LoadingContainer>
        );

      case LoadingState.patience: // User chose "Keep waiting" - shows patience message while continuing to wait
        return (
          <LoadingContainer>
            <TextContent
              title={OPEN_BANKING_INCOME_DETAILS.LOADING_TITLE_PATIENCE}
              subtitle={OPEN_BANKING_INCOME_DETAILS.LOADING_SUBTITLE_PATIENCE}
            />
            <Button prominence="low" onClick={() => onCancel()}>
              Try another option
            </Button>
          </LoadingContainer>
        );

      case LoadingState.cancel: // User chose "Try another option" - shows cancel options with ability to go back
        return (
          <LoadingContainer>
            <TextContent
              title={OPEN_BANKING_INCOME_DETAILS.LOADING_TITLE_CANCEL}
              subtitle={OPEN_BANKING_INCOME_DETAILS.LOADING_SUBTITLE_CANCEL}
            />
            <Inline gap="medium" align="center">
              <Button prominence="low" onClick={handleKeepWaiting}>
                Keep waiting
              </Button>
              <Button prominence="high" onClick={() => onCancel()}>
                Try another option
              </Button>
            </Inline>
          </LoadingContainer>
        );

      case LoadingState.longer_initial_wait: // After 2+ minutes - shows "taking longer than usual" message
        return (
          <LoadingContainer>
            <TextContent
              title={OPEN_BANKING_INCOME_DETAILS.LOADING_TITLE_POLLING}
              subtitle={OPEN_BANKING_INCOME_DETAILS.LOADING_SUBTITLE_POLLING}
            />
          </LoadingContainer>
        );

      case LoadingState.initial: // 0-2 minutes - shows basic "verifying income" message
      default:
        return (
          <LoadingContainer>
            <TextContent
              title={OPEN_BANKING_INCOME_DETAILS.LOADING_TITLE}
              subtitle={OPEN_BANKING_INCOME_DETAILS.LOADING_SUBTITLE}
            />
          </LoadingContainer>
        );
    }
  };

  return renderContent();
}
